import { Component, forwardRef, OnDestroy, OnInit, Renderer2 } from '@angular/core'
import { IcosCascaderPoprs, Option } from './cascader-props'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'

@Component({
  selector: 'icos-cascader',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IcosCascader),
      multi: true
    }
  ],
  template: `
    <span
      [class]="
        'icos-cascader ' +
        (menuVisible ? 'is-opened ' : '') +
        (icosDisabled ? 'is-disabled ' : '') +
        (size ? 'icos-cascader--' + size : '')
      "
      (click)="clickHandle($event)"
      (mouseenter)="inputHover = true"
      (mouseleave)="inputHover = false"
    >
      <icos-input
        [readonly]="true"
        [placeholder]="currentLabels.length ? '' : placeholder"
        [size]="size"
        [icosDisabled]="icosDisabled"
        [icon]="showClearIcon() ? 'circle-close' : 'caret-bottom'"
        [iconClass]="showClearIcon() ? 'icos-cascader__clearIcon' : menuVisible ? 'is-reverse' : ''"
        (icon-click)="clearValue($event)"
      >
      </icos-input>

      <span class="icos-cascader__label" *ngIf="currentLabels.length">
        <ng-container *ngIf="allLevels">
          <ng-container *ngFor="let item of currentLabels; let i = index">
            {{ item.label }}
            <span *ngIf="i < currentLabels.length - 1"> / </span>
          </ng-container>
        </ng-container>
        <ng-container *ngIf="!allLevels">
          {{ currentLabels[currentLabels.length - 1].label }}
        </ng-container>
      </span>
      <icos-cascader-menu></icos-cascader-menu>
    </span>
  `
})
export class IcosCascader extends IcosCascaderPoprs implements OnInit, OnDestroy, ControlValueAccessor {
  steps: any[] = []
  menuVisible: boolean = false
  inputHover: boolean = false
  currentLabels: Option[] = []
  globalListenFunc: Function

  constructor(private renderer: Renderer2) {
    super()
  }

  close(): void {
    this.menuVisible = false
    this.globalListenFunc && this.globalListenFunc()
  }

  clickHandle(event: MouseEvent): void {
    event.stopPropagation()
    if (this.icosDisabled) return

    const element: HTMLElement = event.target as HTMLElement
    const isSelfTrigger = ['SPAN', 'I', 'INPUT'].find(v => v === element.tagName)
    if (!isSelfTrigger) return
    this.menuVisible = !this.menuVisible

    if (this.menuVisible) {
      this.globalListenFunc = this.renderer.listen('document', 'click', () => {
        this.menuVisible = false
      })
    } else {
      this.globalListenFunc && this.globalListenFunc()
    }
  }

  changeLabels(): void {
    const nextValue: Option[] = []
    this.steps.forEach((items: Option[]) => {
      const steps: Option[] = items.filter((item: Option) => item.active)
      nextValue.push(steps[0])
    })
    this.currentLabels = nextValue
    const next = nextValue.map((item: Option) => item.value)
    this.model = next
    this.modelChange.emit(next)
    this.controlChange(next)
  }

  clearValue(event?: Event): void {
    event && event.stopPropagation()
    this.currentLabels = []
    const step1 = this.options.map((option: Option) =>
      Object.assign(option, {
        active: false
      })
    )
    this.steps = [step1]
    this.menuVisible = false
  }

  selectHandle(event: Event, step: number, index: number): any {
    event.stopPropagation()

    if (this.steps[step][index].icosDisabled) return
    this.steps[step] = this.steps[step].map((item: Option, i: number) => Object.assign(item, { active: i === index }))
    // reset steps
    this.steps.length = step + 1
    const next = this.steps[step][index].children

    // go next
    if (next && Array.isArray(next)) {
      // change on select (props)
      this.changeOnSelect && this.changeLabels()
      const nativeNext = next.map((item: Option) => Object.assign(item, { active: false }))
      return this.steps.push(nativeNext)
    }
    // last step
    this.changeLabels()
    this.menuVisible = false
  }

  showClearIcon(): boolean {
    return !!(this.clearable && this.inputHover && this.currentLabels.length)
  }

  ngOnInit(): void {
    this.clearValue()
    if (this.model && this.model.length) {
      const getLabel = (optionList: Option[], value: string) => {
        const item: Option = optionList.filter((option: Option) => option.value === value)[0]
        return { children: item.children, val: item }
      }

      let options: Option[] = this.options
      const val: Option[] = this.model.map(v => {
        const { children, val: value } = getLabel(options, v)
        options = children
        return value
      })
      this.currentLabels = val.filter(v => !!v)
    }
  }

  ngOnDestroy(): void {
    this.globalListenFunc && this.globalListenFunc()
  }

  writeValue(value: any): void {
    this.model = value
  }

  registerOnChange(fn: Function): void {
    this.controlChange = fn
  }

  registerOnTouched(fn: Function): void {
    this.controlTouch = fn
  }

  private controlChange: Function = () => {}
  private controlTouch: Function = () => {}
}
